/**
 * Author: Claude Code
 * Version: v3.2.98
 * Modified: 2025-10-13
 *
 * LoadingScene - P0.4 Game Loading Buffer System (丝滑平滑版)
 * 在waiting room和battle scene之间的过渡场景，显示游戏初始化进度
 *
 * v3.2.98更新: 丝滑平滑加载 + 高清晰度优化
 * - 50ms精确更新，个位数精确进度
 * - 高清文本渲染（更大字号 + 描边）
 */

import * as Phaser from 'phaser';
import { LoadingProgressManager } from '../ui/LoadingProgressManager';
import GameContainer from '../GameContainer'; // use shared scene data (roomId)
import webSocketService from '../../services/websocket/WebSocketService';

// Mock loading event types
interface LoadingStartedEvent {
  eventType: 'game:loading:started';
  data: {
    roomId: string;
    totalStages: number;
    estimatedDuration: number;
    participantCount: number;
  };
}

interface LoadingProgressEvent {
  eventType: 'game:loading:progress';
  data: {
    roomId: string;
    stage: string;
    stageIndex: number;
    totalStages: number;
    percentage: number;
    message: string;
  };
}

interface LoadingCompletedEvent {
  eventType: 'game:loading:completed';
  data: {
    roomId: string;
    totalDuration: number;
    readyToStart: boolean;
  };
}

export class LoadingScene extends Phaser.Scene {
  private progressManager: LoadingProgressManager | null = null;
  private mockEventTimer: Phaser.Time.TimerEvent | null = null;
  private wsHandlersBound = false;
  private transitioned = false;

  constructor() {
    super({ key: 'LoadingScene' });
  }

  create(): void {
    console.log('🎮 LoadingScene: Creating loading scene');

    // Check Rex UI availability（缺失时仅降级UI，不中断真实事件绑定与场景切换）
    if (!this.rexUI) {
      console.error('❌ LoadingScene: Rex UI plugin not found! (UI degraded, will still proceed)');
    }

    // Create background
    this.cameras.main.setBackgroundColor(0x1a1a2e);

    // Title text - 高清晰度配置
    const titleText = this.add.text(
      this.cameras.main.width / 2,
      100,
      'Agent Battle Royale 3.0',
      {
        fontSize: '42px',
        color: '#ffffff',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2
      }
    ).setOrigin(0.5);

    const subtitleText = this.add.text(
      this.cameras.main.width / 2,
      155,
      'Loading Game...',
      {
        fontSize: '20px',
        color: '#aaaaaa',
        fontFamily: 'Arial, sans-serif'
      }
    ).setOrigin(0.5);

    // Create progress manager（RexUI 缺失时跳过进度条，仅靠日志）
    if (this.rexUI) {
      this.progressManager = new LoadingProgressManager(this);
      this.progressManager.create();
    }

    // Mock loading simulation is disabled to avoid double-loading visuals
    // (was gated by NODE_ENV === 'development')

    // Bind real backend loading events → 更新进度并在完成后切场
    this.bindWebSocketLoadingHandlers();

    // 兜底：若没等到完成事件，1.5s 后强制切到 BattleScene（后续由 BattleScene 触发握手）
    this.time.delayedCall(1500, () => this.transitionToBattleScene());

    console.log('✅ LoadingScene: Scene created');
  }

  /**
   * 模拟后端加载事件流 - 丝滑平滑版本
   * 每50ms更新一次进度，实现个位数精确的平滑加载
   */
  private startMockEventSimulation(): void {
    console.log('🎬 LoadingScene: Starting smooth mock event simulation');

    const stages = [
      { stage: 'room_init', stageIndex: 1, startPercentage: 0, endPercentage: 20, message: 'Initializing room dependencies...' },
      { stage: 'game_init', stageIndex: 2, startPercentage: 20, endPercentage: 40, message: 'Creating game engine...' },
      { stage: 'agent_registration', stageIndex: 3, startPercentage: 40, endPercentage: 60, message: 'Registering agents...' },
      { stage: 'map_generation', stageIndex: 4, startPercentage: 60, endPercentage: 80, message: 'Generating game map...' },
      { stage: 'asset_preload', stageIndex: 5, startPercentage: 80, endPercentage: 100, message: 'Preloading assets...' }
    ];

    let currentStageIndex = 0;
    let currentPercentage = 0;
    const startTime = Date.now();
    const updateInterval = 50; // 50ms更新一次
    const stageDuration = 1500; // 每阶段1.5秒

    // Emit loading:started
    const startedEvent: LoadingStartedEvent = {
      eventType: 'game:loading:started',
      data: {
        roomId: 'mock-room-001',
        totalStages: 5,
        estimatedDuration: 8000,
        participantCount: 3
      }
    };
    this.progressManager?.onLoadingStarted(startedEvent);

    // 平滑进度更新：每50ms更新一次
    this.mockEventTimer = this.time.addEvent({
      delay: updateInterval,
      callback: () => {
        if (currentStageIndex < stages.length) {
          const stage = stages[currentStageIndex];
          const stageElapsed = (Date.now() - startTime) - (currentStageIndex * stageDuration);
          const stageProgress = Math.min(stageElapsed / stageDuration, 1);

          // 计算当前精确百分比
          currentPercentage = stage.startPercentage + (stage.endPercentage - stage.startPercentage) * stageProgress;
          currentPercentage = Math.min(currentPercentage, stage.endPercentage);

          const progressEvent: LoadingProgressEvent = {
            eventType: 'game:loading:progress',
            data: {
              roomId: 'mock-room-001',
              stage: stage.stage,
              stageIndex: stage.stageIndex,
              totalStages: 5,
              percentage: Math.floor(currentPercentage), // 取整到个位数
              message: stage.message
            }
          };
          this.progressManager?.onLoadingProgress(progressEvent);

          // 当前阶段完成，进入下一阶段
          if (currentPercentage >= stage.endPercentage) {
            currentStageIndex++;
          }
        } else {
          // 所有阶段完成，发送completed事件
          const totalDuration = Date.now() - startTime;
          const completedEvent: LoadingCompletedEvent = {
            eventType: 'game:loading:completed',
            data: {
              roomId: 'mock-room-001',
              totalDuration,
              readyToStart: true
            }
          };
          this.progressManager?.onLoadingCompleted(completedEvent);

          // Stop timer
          if (this.mockEventTimer) {
            this.mockEventTimer.destroy();
            this.mockEventTimer = null;
          }

          // Transition to BattleScene after 1 second
          this.time.delayedCall(1000, () => {
            console.log('🎮 LoadingScene: Transitioning to BattleScene');
            this.transitionToBattleScene();
          });
        }
      },
      loop: true
    });
  }

  private bindWebSocketLoadingHandlers(): void {
    if (this.wsHandlersBound) return;
    this.wsHandlersBound = true;

    const onStarted = (ev: any) => {
      try { this.progressManager?.onLoadingStarted(ev); } catch {}
    };
    const onProgress = (ev: any) => {
      try { this.progressManager?.onLoadingProgress(ev); } catch {}
    };
    const onCompleted = (ev: any) => {
      try { this.progressManager?.onLoadingCompleted(ev); } catch {}
      // 小延迟后切换到 BattleScene，并携带 roomId
      this.time.delayedCall(300, () => this.transitionToBattleScene());
    };

    // 保存到 scene data 便于卸载
    (this as any)._ws_onStarted = onStarted;
    (this as any)._ws_onProgress = onProgress;
    (this as any)._ws_onCompleted = onCompleted;

    webSocketService.onGameEvent('GAME_LOADING_STARTED', onStarted);
    webSocketService.onGameEvent('GAME_LOADING_PROGRESS', onProgress);
    webSocketService.onGameEvent('GAME_LOADING_COMPLETED', onCompleted);
  }

  private transitionToBattleScene(): void {
    if (this.transitioned) return;
    this.transitioned = true;
    try {
      const roomId = GameContainer.getSceneData('roomId');
      if (roomId) {
        this.scene.start('BattleScene', { roomId });
      } else {
        this.scene.start('BattleScene');
      }
    } catch {
      this.scene.start('BattleScene');
    }
  }

  shutdown(): void {
    console.log('🛑 LoadingScene: Shutting down');

    // Clean up mock timer
    if (this.mockEventTimer) {
      this.mockEventTimer.destroy();
      this.mockEventTimer = null;
    }

    // Clean up progress manager
    if (this.progressManager) {
      this.progressManager.destroy();
      this.progressManager = null;
    }

    // Unbind WS handlers
    try {
      if ((this as any)._ws_onStarted) webSocketService.off('GAME_LOADING_STARTED', (this as any)._ws_onStarted);
      if ((this as any)._ws_onProgress) webSocketService.off('GAME_LOADING_PROGRESS', (this as any)._ws_onProgress);
      if ((this as any)._ws_onCompleted) webSocketService.off('GAME_LOADING_COMPLETED', (this as any)._ws_onCompleted);
    } catch {}
  }
}
